// Unified session helper for Arabell portals (Member / Staff / Admin)
const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "arabell_session_jwt";
const USER_KEY = "arabell_session_user";

export const getSessionToken = () => localStorage.getItem(TOKEN_KEY);
export const getSessionUser = () => {
  try { return JSON.parse(localStorage.getItem(USER_KEY) || "null"); }
  catch { return null; }
};
export const setSession = (token, user) => {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
};
export const clearSession = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
};

const auth = () => ({ Authorization: `Bearer ${getSessionToken()}` });

// Errori con status HTTP e codice applicativo (detail stringa o {code, message}).
const handle = async (res) => {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const d = data.detail;
    const err = new Error(typeof d === "string" ? d : (d && d.message) || `Errore (${res.status})`);
    err.status = res.status;
    err.code = d && typeof d === "object" ? d.code : undefined;
    throw err;
  }
  return data;
};

// Ask browser for GPS — returns null if denied/unavailable. 2.5s timeout
// so it never blocks the login button. The user's choice is remembered by
// the browser; we don't store it anywhere.
export const getBrowserGps = () => new Promise((resolve) => {
  if (typeof navigator === "undefined" || !navigator.geolocation) {
    resolve(null);
    return;
  }
  let done = false;
  const finish = (val) => { if (!done) { done = true; resolve(val); } };
  navigator.geolocation.getCurrentPosition(
    (pos) => finish({
      gps_lat: pos.coords.latitude,
      gps_lon: pos.coords.longitude,
      gps_accuracy: pos.coords.accuracy,
    }),
    () => finish(null),
    { enableHighAccuracy: true, timeout: 2500, maximumAge: 60000 },
  );
  // Safety timeout in case the browser ignores `timeout`
  setTimeout(() => finish(null), 3000);
});

export const sessionApi = {
  login: async (username, password) => {
    const gps = await getBrowserGps();
    return fetch(`${API}/api/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username, password, ...(gps || {}) }),
    }).then(handle);
  },
  me: () =>
    fetch(`${API}/api/auth/me`, { headers: auth() }).then(handle),
  memberProfile: () =>
    fetch(`${API}/api/member/profile`, { headers: auth() }).then(handle),
  updateMemberProfile: (patch) =>
    fetch(`${API}/api/member/profile`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...auth() },
      body: JSON.stringify(patch),
    }).then(handle),
  memberQuotes: () =>
    fetch(`${API}/api/member/quotes`, { headers: auth() }).then(handle),
  memberSimulations: () =>
    fetch(`${API}/api/member/simulations`, { headers: auth() }).then(handle),
  memberWallet: () =>
    fetch(`${API}/api/member/wallet`, { headers: auth() }).then(handle),
  cardos: (path = "", options = {}) =>
    fetch(`${API}/api/member/cardos${path}`, {
      ...options, headers: { "Content-Type": "application/json", ...auth(), ...(options.headers || {}) },
    }).then(handle),
  memberContracts: () =>
    fetch(`${API}/api/member/contracts`, { headers: auth() }).then(handle),
  memberContractPdfUrl: (kind, contractId) =>
    `${API}/api/member/contracts/${kind}/${contractId}/pdf`,
  memberTimeline: () =>
    fetch(`${API}/api/member/timeline`, { headers: auth() }).then(handle),
  staffProfile: () =>
    fetch(`${API}/api/collaboratori/profile`, { headers: auth() }).then(handle),
  staffClientsList: () =>
    fetch(`${API}/api/staff/clients-list`, { headers: auth() }).then(handle),
  staffDashboard: () =>
    fetch(`${API}/api/staff/dashboard`, { headers: auth() }).then(handle),
  staffCreateClient: (data) =>
    fetch(`${API}/api/staff/clients`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...auth() },
      body: JSON.stringify(data),
    }).then(handle),
  staffClients: ({ search = "", type = "" } = {}) => {
    const qs = new URLSearchParams();
    if (search) qs.set("search", search);
    if (type) qs.set("type", type);
    const q = qs.toString();
    return fetch(`${API}/api/staff/clients${q ? `?${q}` : ""}`, { headers: auth() }).then(handle);
  },
  staffClientFull: (id) =>
    fetch(`${API}/api/staff/clients/${id}/full`, { headers: auth() }).then(handle),
};
